// Environment-specific Pulse AS2 IDs (TSF-1949).
//
// A newly created AS2 connection config defaults its `pulse_as2_id` to a value
// that encodes the environment it was created in: bare `RITHUMAS2` in prod, and
// a `-<ENV>` suffixed form everywhere else. The value therefore is NOT stable
// across environments — the same logical config wears a different id in each
// one.
//
// Cross-environment copy has to treat those spellings as one identity:
// analysis matches a source config against the target environment's form,
// the confirm modal shows the conversion, and apply writes the target form.
// See `normalizePulseAs2IdForEnvironment`.
//
// This lives in `common/` because the API (analyze + apply) and the UI (the
// config form's placeholder and the copy preview label) must agree on exactly
// which spellings are the environment defaults. A drifted copy would let one
// side convert and the other not.

export const pulseAs2IdByEnvironment: Record<string, string> = {
  prod: 'RITHUMAS2',
  staging: 'RITHUMAS2-STAGING',
  test: 'RITHUMAS2-TEST',
  dev: 'RITHUMAS2-DEV',
};

// True when the id is the default for the environment it was read from.
//
// The check is scoped to `sourceEnvironment` rather than to the set of all four
// spellings: a config sitting in staging under `RITHUMAS2-DEV` is not staging's
// default, so it is a value somebody set deliberately — opaque to us, and it
// must survive a copy byte-for-byte like any other partner-specific id.
export const isDefaultPulseAs2Id = (
  pulseAs2Id: string | undefined,
  sourceEnvironment: string,
): boolean => pulseAs2Id !== undefined && pulseAs2IdByEnvironment[sourceEnvironment] === pulseAs2Id;

// Converts the source environment's default Pulse AS2 ID to the target
// environment's form, e.g. `RITHUMAS2-STAGING` → `RITHUMAS2` for staging → prod.
//
// Returns the input unchanged for an id that is not the source environment's
// default, an undefined id, or a target environment with no known default
// (`mock` in tests, or anything new). Never invents an id: a config without one
// still has none afterwards.
export const normalizePulseAs2IdForEnvironment = (
  pulseAs2Id: string | undefined,
  sourceEnvironment: string,
  targetEnvironment: string,
): string | undefined => {
  if (!isDefaultPulseAs2Id(pulseAs2Id, sourceEnvironment)) {
    return pulseAs2Id;
  }

  return pulseAs2IdByEnvironment[targetEnvironment] ?? pulseAs2Id;
};

// True when copying this id from `sourceEnvironment` into `targetEnvironment`
// changes it — the signal the confirm modal uses to show the conversion to the
// onboarding team.
export const pulseAs2IdChangesInEnvironment = (
  pulseAs2Id: string | undefined,
  sourceEnvironment: string,
  targetEnvironment: string,
): boolean =>
  normalizePulseAs2IdForEnvironment(pulseAs2Id, sourceEnvironment, targetEnvironment) !== pulseAs2Id;
